<script lang="ts">
import {
  defineComponent,
  PropType,
  reactive,
  computed,
  onMounted,
} from 'vue'
import stateInitializer from '@/models/stateInitializer'
import SectionForIndex from './_partials/SectionForIndex.vue'
import SectionForCategory from './_partials/SectionForCategory.vue'
import MaskContentForGuest from '@/components/cta/MaskContentForGuest.vue'
import { useUserStore } from '@/stores/user'

export default defineComponent({
  name: 'SearchIndexMain',

  components: {
    SectionForIndex,
    SectionForCategory,
    MaskContentForGuest,
  },

  props: {
    /**
     * 検索文言
     */
    searchWord: {
      type: String,
      default: '',
    },

    categories: {
      type: Object as PropType<Record<SearchCategory, { key: SearchCategory, label: string }>>,
      required: true,
    },

    currentTabKey: {
      type: String as PropType<SearchCategory>,
      required: true,
    },

    /**
     * クエリパラメータ用の key 情報
     */
    queryKeys: {
      type: Object as PropType<Record<'KEYWORD' | 'FILTER', string>>,
      required: true,
    },

  },

  emits: ['update:resultTotal'],

  setup(props, { emit }) {
    // store
    const userStore = useUserStore()

    // data
    interface State {
      search: {
        form: Form,
        input: {
          value: string,
          name: string,
        },
      },
      searchResult: {
        keyword: SearchResult,
        report: SearchResult,
        article: SearchResult,
        document: SearchResult,
      }
    }
    const state: State = reactive({
      search: {
        form: {
          action: '',
          method: 'get',
        },
        input: {
          value: '',
          name: 'keyword',
        },
      },
      searchResult: {
        keyword: {
          totalCount: 0,
          items: [],
        },
        report: {
          totalCount: 0,
          items: [],
        },
        article: {
          totalCount: 0,
          items: [],
        },
        document: {
          totalCount: 0,
          items: [],
        },
      }
    })
    stateInitializer(state, 'main')

    /**
     * 特定のカテゴリータブが表示されている場合の選択カテゴリーの情報
     * 「すべて」の場合は undefined
     */
    const currentCategoryResult = computed<SearchResult | undefined>(() => {
      const _key = props.currentTabKey as keyof typeof state.searchResult
      return _key && state.searchResult[_key]
        ? state.searchResult[_key]
        : undefined
    })

    /**
     * 「すべて」タブかどうか
     */
    const isIndex = computed<boolean>(() => props.currentTabKey === 'index')

    /**
     * すべてのカテゴリの合計検索結果数
     */
    const allSearchResultTotal = computed<number>(() => {
      return Object.values(state.searchResult).reduce((acc, result) => acc + result.totalCount, 0)
    })

    /**
     * 実際に画面に描画する検索結果数
     */
    const renderingSearchResultTotal = computed<number | undefined>(() => {
      return isIndex.value
        ? allSearchResultTotal.value
        : currentCategoryResult.value?.totalCount
    })

    /**
     * コンテンツ先頭に検索結果数を表示するかどうか
     *
     * @description
     *  ログイン済み
     *    - 「すべて」タブ：表示しない
     *    - カテゴリータブ：表示する
     *  未ログイン
     *    - 「すべて」タブ：表示する
     *    - カテゴリータブ：表示する
     */
    const isShowSearchResult = computed<boolean>(() => {
      return !(userStore.context.isSignedIn && props.currentTabKey === 'index')
    })

    /**
     * 検索結果あるかどうか
     */
    const hasResult = computed<boolean>(() => allSearchResultTotal.value > 0)

    onMounted(() => {
      emit('update:resultTotal', allSearchResultTotal.value)
    })

    return {
      // store
      userStore,

      // data
      state,

      // computed
      currentCategoryResult,
      renderingSearchResultTotal,
      isShowSearchResult,
      isIndex,
      hasResult,
    }
  },
})
</script>

<template>
  <section class="section">
    <!-- 検索結果あり -->
    <div
      v-if="hasResult"
      class="l-unit__searchResult">
      <!-- 件数表示 -->
      <div
        v-if="isShowSearchResult"
        class="l-unit__searchResult--header">
        <div class="o-label__searchResultTotal">
          <p class="o-label__searchResultTotal--item">
            {{ renderingSearchResultTotal }}件の結果
          </p>
        <!-- /.o-label__searchResultTotal --></div>
      <!-- /.l-unit__searchResult header --></div>

      <div class="l-unit__searchResult--description">
        <!-- 「すべて」タブ表示時 -->
        <SectionForIndex
          v-if="isIndex"
          :search-word="searchWord"
          :all-search-result="state.searchResult"
          :categories="categories"
          :query-keys="queryKeys" />

        <!-- カテゴリータブ表示時 -->
        <SectionForCategory
          v-else-if="currentCategoryResult"
          :search-word="searchWord"
          :category-search-result="currentCategoryResult" />
      <!-- /.l-unit__searchResult description --></div>

      <!-- 未ログイン時のログイン CTA -->
      <div
        v-if="!userStore.context.isSignedIn"
        class="l-unit__searchResult--action">
        <MaskContentForGuest />
      <!-- /.l-unit__searchResult header --></div>
    <!-- /.l-unit__searchResult --></div>

    <!-- 検索結果 0 件 -->
    <template v-else>
      <div class="o-heading__noResult">
        <h1 class="o-heading__noResult--item">
          「{{ searchWord }}」に一致する情報は見つかりませんでした。
        </h1>
      <!-- /.o-heading__noResult --></div>

      <div class="o-list__searchHint">
        <dl class="o-list__searchHint--unit">
          <dt class="o-list__searchHint--term">
            検索のヒント
          </dt>
          <dd class="o-list__searchHint--description">
            誤字・脱字はありませんか？
          </dd>
          <dd class="o-list__searchHint--description">
            複数のワードはスペースで区切られていますか？
          </dd>
          <dd class="o-list__searchHint--description">
            検索しているワードの数は多くありませんか？
          </dd>
        </dl>
      <!-- /.o-list__searchHint --></div>
    </template>
  </section>
</template>

<style lang="sass" scoped>
.l-unit__searchResult
  position: relative

  & &--header
    margin-bottom: changeSizePx2Em(24)

.o-heading__noResult
  & &--item
    font:
      size: $font-normal
      weight: bold
    margin-bottom: 1em

.o-list__searchHint
  & &--unit
    line-height: 1.6

  & &--term
    position: relative
    margin-bottom: 0.5em

    &::after
      content: "："

  $bullet-size: 0.3em
  $margin-size: 0.6em
  & &--description
    position: relative
    text-indent: -($bullet-size + $margin-size)
    margin-top: changeSizePx2Em(4)
    padding-left: ($bullet-size + $margin-size)

    &:first-child
      margin-top: 0

    &::before
      content: ' '
      width: $bullet-size
      height: $bullet-size
      display: inline-block
      text-align: left
      text-indent: 0
      vertical-align: middle
      margin:
        top: -0.2em
        right: $margin-size
      background-color: rgba($bg-black, 1)
      box-sizing: border-box
      border-radius: 50%

.o-label__searchResultTotal
  & &--item
    line-height: 1
    color: $font-gray
    font-size: $font-x-small
</style>
